import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiPlay, FiCheck, FiStar, FiPhone, FiMail, FiUser, FiBriefcase, FiChevronDown, FiSearch, FiFilter, FiMessageCircle } from 'react-icons/fi';
import './App.css';
import HeroSection from './components/HeroSection';
import WorkshopTimer from './components/WorkshopTimer';
import { useWorkshopSchedule } from './hooks/useWorkshopSchedule';

// Thank You Page URL
const THANK_YOU_PAGE_URL = 'http://thxworkingprofessions.10xaifounder.com/';

// Pabbly Webhook URL
const WEBHOOK_URL = 'https://connect.pabbly.com/webhook-listener/webhook/IjU3NjIwNTY1MDYzMDA0MzQ1MjZmIg_3D_3D_pc/IjU3NjcwNTZjMDYzNzA0MzA1MjZlNTUzNjUxMzEi_pc';

// WhatsApp Support Number
const WHATSAPP_NUMBER = '+919940890569';
const WHATSAPP_MESSAGE = encodeURIComponent(
  'Hi! I need help with the Digital Income Workshop registration.'
);

// Animation Variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      ease: 'easeInOut'
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 100,
      damping: 12
    }
  }
};

const sectionFadeIn = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.7, ease: 'easeInOut' },
  viewport: { once: true, amount: 0.2 }
};

// Video testimonials data
const testimonialVideos = [
  {
    id: 1,
    url: 'https://youtu.be/vRzrNK9XPmQ?si=HcsiRvsrjOQhzfiO',
    embedId: 'vRzrNK9XPmQ',
    thumbnail: 'https://img.youtube.com/vi/vRzrNK9XPmQ/maxresdefault.jpg',
    title: 'Want More Customers? 🤔 See How Manivanan Did It with FunnelsDone',
    name: 'Dr. Valarmathi'
  },
  {
    id: 2,
    url: 'https://www.youtube.com/watch?v=v3EEtDIaC3w',
    embedId: 'v3EEtDIaC3w',
    thumbnail: 'https://img.youtube.com/vi/v3EEtDIaC3w/maxresdefault.jpg',
    title: 'How Funnels Done Transformed My Coaching Business',
    name: 'A Woman Entrepreneur Coach'
  },
  {
    id: 3,
    url: 'https://youtu.be/IrfPwkcSTB0?si=4zR7J5O2aIbdr7k0',
    embedId: 'IrfPwkcSTB0',
    thumbnail: 'https://img.youtube.com/vi/IrfPwkcSTB0/maxresdefault.jpg',
    title: 'From Struggling to 2 Lakhs: My Funnels Done Success Story',
    name: 'Bridal Lead Generation Expert Coach'
  },
  {
    id: 4,
    url: 'https://youtu.be/F26JiMS-w4I?si=5CoJ4w0AnUAMPYZm',
    embedId: 'F26JiMS-w4I',
    thumbnail: 'https://img.youtube.com/vi/F26JiMS-w4I/maxresdefault.jpg',
    title: 'Overcoming Coaching Business Challenges with Funnels Done',
    name: 'Coaching Business'
  },
  {
    id: 5,
    url: 'https://youtu.be/yWzJt7jv5CM?si=cFmZMEoNQgvFxmh4',
    embedId: 'yWzJt7jv5CM',
    thumbnail: 'https://img.youtube.com/vi/yWzJt7jv5CM/maxresdefault.jpg',
    title: 'How I Made 30 Lakhs in a Year by Helping a Lot of People as a Coach without Quiting my Job',
    name: 'Professor'
  },
  {
    id: 6,
    url: 'https://youtube.com/shorts/L0jJ2NHIQ8w?si=vDvB1EIcSDn0lzHX',
    embedId: 'L0jJ2NHIQ8w',
    thumbnail: 'https://img.youtube.com/vi/L0jJ2NHIQ8w/maxresdefault.jpg',
    title: 'The Secret Behind Mariyappan Business Growth 🔥 | FunnelsDone AI',
    name: 'Iniya Jewelers Madurai'
  }
];

// Client Feedback data
const clientFeedbacks = [
  {
    id: 1,
    name: 'Maria Sundar',
    role: 'Stock Trader Coach',
    image: 'https://storage.files-vault.com/uploads/1760942061-nsFEgCbsBd.webp',
    quote: 'Crossed 1000 members for level 1 in the last 60 days. On track for ₹10 Lakhs/month implementing this business model.'
  },
  {
    id: 2,
    name: 'A R Ahamed Junaith',
    role: 'English Confidence Building Coach',
    image: 'https://storage.files-vault.com/uploads/1760941910-ht57JBdJ4f.webp',
    quote: 'Crossed over ₹21+ Lakhs Revenue in 10 Months while working as a College Professor.'
  },
  {
    id: 3,
    name: 'Subiya Fathima',
    role: 'Bridal Lead Generation Expert',
    image: 'https://storage.files-vault.com/uploads/1760942062-cWbhjZc7n9.webp',
    quote: 'Sold my ₹5999 course to 19 people in a single day. Cleared all debts and school fees after struggling financially.'
  },
  {
    id: 4,
    name: 'Arjumand',
    role: 'Quran & Spiritual Coach',
    image: 'https://storage.files-vault.com/uploads/1760942064-I6HBtoRq5t.webp',
    quote: 'People are taking action and investing in getting a connection with Allah. It is more than coaching, it is a service to society.'
  },
  {
    id: 5,
    name: 'Manigandan',
    role: 'Course Student',
    image: 'https://storage.files-vault.com/uploads/1760942066-tiqyCho8Gf.webp',
    quote: 'Five star for Clarity, Dedication, and Service. Anyone willing to update their knowledge can opt for his courses blindly.'
  },
  {
    id: 6,
    name: 'Priya Sharma',
    role: 'IT Professional',
    image: null,
    quote: 'Work-Life Balance Restored. Income Doubled. Knowledge Impact Multiplied.'
  }
];

// FAQ data
const faqData = [
  {
    id: 1,
    question: 'When and where is this 3-hour workshop happening?',
    answer: "This is a **3-hour LIVE online workshop** held **every Sunday from 10:00 AM to 1:00 PM IST** (India Standard Time). Once you register, you'll receive the secure joining link via email and WhatsApp."
  },
  {
    id: 2,
    question: 'Is this suitable for Working Professionals?',
    answer: 'Yes! This is specifically designed for Working Professionals, IT Professionals, Corporate Employees, Managers, Consultants, and anyone who wants to leverage their expertise digitally and create additional income streams beyond their salary.'
  },
  {
    id: 3,
    question: 'Do I need technical knowledge or coding skills?',
    answer: 'No. This workshop is designed for professionals with no technical background. We guide you through user-friendly AI tools and digital platforms that anyone can use.'
  },
  {
    id: 4,
    question: "Will I get recordings if I can't attend live?",
    answer: 'To maintain the exclusivity and high-impact nature of the live session, recordings will not be provided. We encourage you to attend live to ask questions about your specific situation.'
  },
  {
    id: 5,
    question: 'How will this help me build income beyond my salary?',
    answer: 'We teach you how to convert your knowledge and expertise into digital products and services that work 24/7 — creating income streams that don\'t require your daily presence.'
  },
  {
    id: 6,
    question: 'What happens after I register?',
    answer: "You'll immediately receive a confirmation email and WhatsApp message with your unique joining link, bonus resources, and instructions. You'll also get helpful reminders before the workshop."
  },
  {
    id: 7,
    question: 'What if I have questions about my specific situation?',
    answer: "Upon joining, you get Lifetime Access to our exclusive private community of working professionals. You can ask questions there, and during the live Q&A session at the end of the 3-hour workshop."
  },
  {
    id: 8,
    question: 'Is this really FREE?',
    answer: "Yes! This 3-hour workshop is completely FREE. No hidden charges. We're committed to helping working professionals succeed with digital transformation."
  }
];

// Expanded Country Codes List
const countryCodes = [
  { code: '+91', country: 'IN', name: 'India', flag: '🇮🇳' },
  { code: '+1', country: 'US', name: 'USA', flag: '🇺🇸' },
  { code: '+60', country: 'MY', name: 'Malaysia', flag: '🇲🇾' },
  { code: '+65', country: 'SG', name: 'Singapore', flag: '🇸🇬' },
  { code: '+44', country: 'GB', name: 'UK', flag: '🇬🇧' },
  { code: '+971', country: 'AE', name: 'UAE', flag: '🇦🇪' },
  { code: '+61', country: 'AU', name: 'Australia', flag: '🇦🇺' },
  { code: '+1', country: 'CA', name: 'Canada', flag: '🇨🇦' }
];

// Tracking data extraction function
const getTrackingData = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const locale = navigator.language || 'en-US';

  let detectedCountry = 'Unknown';
  if (timezone && timezone.includes('/')) {
    const cityTimezone = timezone.split('/')[1];
    if (cityTimezone) {
      const timezoneCountryMap = {
        Kolkata: 'India',
        Mumbai: 'India',
        Delhi: 'India',
        Chennai: 'India',
        Bangalore: 'India',
        New_York: 'USA',
        Los_Angeles: 'USA',
        Chicago: 'USA',
        London: 'UK',
        Paris: 'France',
        Berlin: 'Germany',
        Tokyo: 'Japan',
        Singapore: 'Singapore',
        Dubai: 'UAE'
      };
      detectedCountry = timezoneCountryMap[cityTimezone] || 'Unknown';
    }
  }

  return {
    campaign_id: urlParams.get('campaign_id') || '',
    adset_id: urlParams.get('adset_id') || '',
    ad_id: urlParams.get('ad_id') || '',
    fbclid: urlParams.get('fbclid') || '',
    gclid: urlParams.get('gclid') || '',
    campaign_name: urlParams.get('campaign_name') || 'Digital Income Workshop Campaign',
    adset_name: urlParams.get('adset_name') || 'Digital Income Workshop Ad Set',
    ad_name: urlParams.get('ad_name') || 'Digital Income Workshop Ad',
    utm_source: urlParams.get('utm_source') || '',
    utm_medium: urlParams.get('utm_medium') || '',
    utm_campaign: urlParams.get('utm_campaign') || '',
    utm_term: urlParams.get('utm_term') || '',
    utm_content: urlParams.get('utm_content') || '',
    source: urlParams.get('source') || 'organic',
    medium: urlParams.get('medium') || '',
    country: detectedCountry,
    timezone: timezone,
    locale: locale,
    referrer: document.referrer || '',
    page_url: window.location.href,
    page_title: document.title,
    user_agent: navigator.userAgent,
    screen_width: window.screen.width,
    screen_height: window.screen.height,
    timestamp: new Date().toISOString(),
    session_id: sessionStorage.getItem('session_id') || Date.now().toString()
  };
};

// Generate workshop batch ID
const generateWorkshopBatchId = (workshopDate) => {
  if (!workshopDate) return 'BATCH_UNKNOWN';
  const date = new Date(workshopDate);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `BATCH_${year}${month}${day}`;
};

// Get batch name
const getBatchName = (workshopDate) => {
  if (!workshopDate) return 'Sunday Unknown';
  const date = new Date(workshopDate);
  const dayNames = [
    'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'
  ];
  const dayName = dayNames[date.getDay()];
  const day = date.getDate();
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const monthName = monthNames[date.getMonth()];
  return `${dayName} ${day}${getDaySuffix(day)} ${monthName}`;
};

// Get webinar batch ISO 8601 format
const getWebinarBatchISO = (workshopDate) => {
  if (!workshopDate) return new Date().toISOString();
  const date = new Date(workshopDate);
  date.setHours(10, 0, 0, 0);
  return date.toISOString();
};

function getDaySuffix(day) {
  if (day > 3 && day < 21) return 'th';
  switch (day % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
}

// Searchable Country Code Component
const CountryOption = React.memo(({ country, onSelect }) => (
  <li
    className="country-option"
    onClick={() => onSelect(country.code)}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') onSelect(country.code);
    }}
    role="option"
    tabIndex={0}
  >
    <span className="country-flag">{country.flag}</span>
    <span className="country-name">{country.name}</span>
    <span className="country-code">{country.code}</span>
  </li>
));

function SearchableCountryCodeSelect({ value, onChange, options }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  const selectedCountry = useMemo(
    () => options.find((c) => c.code === value) || options[0],
    [value, options]
  );

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    const lowerSearch = searchTerm.toLowerCase();
    return options.filter(
      (country) =>
        country.name.toLowerCase().includes(lowerSearch) ||
        country.code.includes(lowerSearch) ||
        country.country.toLowerCase().includes(lowerSearch)
    );
  }, [searchTerm, options]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (code) => {
    onChange(code);
    setIsOpen(false);
    setSearchTerm('');
  };

  return (
    <div className="searchable-dropdown" ref={dropdownRef}>
      <button
        type="button"
        className="country-select-button"
        onClick={() => setIsOpen(!isOpen)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="country-flag">{selectedCountry.flag}</span>
        <span>{selectedCountry.code}</span>
        <FiChevronDown className={`chevron-icon ${isOpen ? 'expanded' : ''}`} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="dropdown-menu"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            <div className="search-input-wrapper">
              <FiSearch className="search-icon" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search country..."
                className="search-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <ul className="dropdown-list" role="listbox">
              {filteredOptions.length > 0 ? (
                filteredOptions.map((country) => (
                  <CountryOption
                    key={country.country}
                    country={country}
                    onSelect={handleSelect}
                  />
                ))
              ) : (
                <li className="country-option-empty">No results found</li>
              )}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// WhatsApp Support Button Component
function WhatsAppSupportButton() {
  const handleWhatsAppClick = () => {
    const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_MESSAGE}`;
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');

    if (typeof fbq !== 'undefined') {
      fbq('track', 'Contact', {
        content_name: 'WhatsApp Support Click',
        content_category: 'Customer Support'
      });
    }
  };

  return (
    <motion.button
      className="whatsapp-support-button"
      onClick={handleWhatsAppClick}
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ type: 'spring', stiffness: 260, damping: 20, delay: 2 }}
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.95 }}
      aria-label="Contact us on WhatsApp"
    >
      <FiMessageCircle className="whatsapp-icon" />
      <span className="whatsapp-tooltip">Need Help? Chat with us!</span>
    </motion.button>
  );
}

function App() {
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+91',
    phone: '',
    profession: '',
    consent: false
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [faqSearch, setFaqSearch] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [selectedRoleFilter, setSelectedRoleFilter] = useState('all');
  const [expandedFeedback, setExpandedFeedback] = useState({});
  const [hasAutoPopupShown, setHasAutoPopupShown] = useState(false);

  const modalRef = useRef(null);
  const firstInputRef = useRef(null);
  const autoPopupTimeoutRef = useRef(null);

  const { workshopDetails } = useWorkshopSchedule();

  useEffect(() => {
    if (!sessionStorage.getItem('session_id')) {
      sessionStorage.setItem('session_id', Date.now().toString());
    }
  }, []);

  useEffect(() => {
    if (!hasAutoPopupShown && !isFormModalOpen) {
      autoPopupTimeoutRef.current = setTimeout(() => {
        setIsFormModalOpen(true);
        setHasAutoPopupShown(true);

        if (typeof fbq !== 'undefined') {
          fbq('track', 'ViewContent', {
            content_name: 'Digital Income Workshop - Auto Popup',
            content_category: 'Workshop',
            content_type: 'auto_popup',
            value: 0,
            currency: 'INR'
          });
        }
      }, 10000);
    }

    return () => {
      if (autoPopupTimeoutRef.current) {
        clearTimeout(autoPopupTimeoutRef.current);
        autoPopupTimeoutRef.current = null;
      }
    };
  }, [hasAutoPopupShown, isFormModalOpen]);

  useEffect(() => {
    if (isFormModalOpen && firstInputRef.current) {
      firstInputRef.current.focus();
    }

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsFormModalOpen(false);
        setSelectedVideo(null);
      }
    };

    if (isFormModalOpen || selectedVideo) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isFormModalOpen, selectedVideo]);

  const handleFaqSearch = useCallback(
    (value) => {
      setFaqSearch(value);
      if (searchTimeout) clearTimeout(searchTimeout);
      const newTimeout = setTimeout(() => {}, 300);
      setSearchTimeout(newTimeout);
    },
    [searchTimeout]
  );

  const filteredFaqs = useMemo(() => {
    if (!faqSearch.trim()) return faqData;
    return faqData.filter(
      (faq) =>
        faq.question.toLowerCase().includes(faqSearch.toLowerCase()) ||
        faq.answer.toLowerCase().includes(faqSearch.toLowerCase())
    );
  }, [faqSearch]);

  const filteredFeedbacks = useMemo(() => {
    if (selectedRoleFilter === 'all') return clientFeedbacks;
    return clientFeedbacks.filter((feedback) =>
      feedback.role.toLowerCase().includes(selectedRoleFilter.toLowerCase())
    );
  }, [selectedRoleFilter]);

  const uniqueRoles = useMemo(
    () => ['all', ...new Set(clientFeedbacks.map((f) => f.role))],
    []
  );

  const toggleFaq = (faqId) => setExpandedFaq(expandedFaq === faqId ? null : faqId);

  const toggleFeedbackExpanded = (feedbackId) =>
    setExpandedFeedback((prev) => ({ ...prev, [feedbackId]: !prev[feedbackId] }));

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Name is required';
    if (!formData.email.trim()) errors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email))
      errors.email = 'Please enter a valid email';

    if (!formData.phone.trim()) errors.phone = 'Phone number is required';
    else if (!/^\d{7,15}$/.test(formData.phone.replace(/\s/g, '')))
      errors.phone = 'Please enter a valid phone number';

    if (!formData.profession.trim()) errors.profession = 'Profession is required';
    if (!formData.consent) errors.consent = 'Please agree to the terms';

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm() || isSubmitting) return;

    setIsSubmitting(true);

    try {
      const phoneWithCountryCode = `${formData.countryCode}${formData.phone}`;
      const trackingData = getTrackingData();
      const workshopBatchId = generateWorkshopBatchId(workshopDetails.date);
      const workshopBatchName = getBatchName(workshopDetails.date);
      const webinarBatchISO = getWebinarBatchISO(workshopDetails.date);

      const selectedCountry = countryCodes.find((c) => c.code === formData.countryCode) || {
        country: 'Unknown',
        name: 'Unknown'
      };

      const webhookPayload = {
        first_name: formData.name,
        email: formData.email,
        phone_no: phoneWithCountryCode,
        profession: formData.profession,
        workshop_batch: workshopBatchId,
        batch_name: workshopBatchName,
        batch_date: workshopDetails.formattedDate,
        batch_time: workshopDetails.timeString,
        webinar_batch_iso: webinarBatchISO,
        amount: '0',
        payment_status: 'free',
        registration_type: 'free_workshop',
        campaign_id: trackingData.campaign_id,
        adset_id: trackingData.adset_id,
        ad_id: trackingData.ad_id,
        campaign_name: trackingData.campaign_name,
        adset_name: trackingData.adset_name,
        ad_name: trackingData.ad_name,
        fbclid: trackingData.fbclid,
        gclid: trackingData.gclid,
        utm_source: trackingData.utm_source,
        utm_medium: trackingData.utm_medium,
        utm_campaign: trackingData.utm_campaign,
        utm_term: trackingData.utm_term,
        utm_content: trackingData.utm_content,
        country: trackingData.country,
        timezone: trackingData.timezone,
        locale: trackingData.locale,
        phone_country_code: selectedCountry.country,
        phone_country_name: selectedCountry.name,
        phone_dial_code: formData.countryCode,
        source: trackingData.source,
        medium: trackingData.medium,
        referrer: trackingData.referrer,
        page_url: trackingData.page_url,
        session_id: trackingData.session_id,
        registration_timestamp: trackingData.timestamp,
        product_name: 'Digital Income Workshop',
        currency: 'INR'
      };

      try {
        const webhookResponse = await fetch(WEBHOOK_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify(webhookPayload)
        });

        if (!webhookResponse.ok) {
          console.warn('Webhook response not OK:', webhookResponse.status);
        }

        console.log('✅ Webhook data sent:', webhookPayload);
      } catch (webhookError) {
        console.warn('⚠️ Webhook submission failed:', webhookError);
      }

      if (typeof fbq !== 'undefined') {
        fbq('track', 'CompleteRegistration', {
          content_name: 'Digital Income Workshop Registration',
          content_category: 'Workshop',
          value: 0,
          currency: 'INR',
          ...(trackingData.campaign_id && { campaign_id: trackingData.campaign_id }),
          ...(trackingData.adset_id && { adset_id: trackingData.adset_id }),
          ...(trackingData.ad_id && { ad_id: trackingData.ad_id }),
          custom_batch_id: workshopBatchId,
          custom_batch_name: workshopBatchName
        });
      }

      setShowSuccess(true);
      setIsFormModalOpen(false);
      setFormData({
        name: '',
        email: '',
        countryCode: '+91',
        phone: '',
        profession: '',
        consent: false
      });

      setTimeout(() => {
        window.location.href = THANK_YOU_PAGE_URL;
      }, 1500);
    } catch (error) {
      console.error('❌ Form submission error:', error);
      alert('There was an error submitting the form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) setFormErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const getInitials = (name) =>
    name
      .split(' ')
      .map((word) => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);

  const handleManualModalOpen = () => {
    if (autoPopupTimeoutRef.current) {
      clearTimeout(autoPopupTimeoutRef.current);
      autoPopupTimeoutRef.current = null;
    }
    setHasAutoPopupShown(true);
    setIsFormModalOpen(true);
  };

  return (
    <>
      <div className="app">
        <AnimatePresence>
          {showSuccess && (
            <motion.div
              className="success-toast"
              initial={{ opacity: 0, y: -50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -50 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <FiCheck className="success-icon" />
              Registration successful! Redirecting to confirmation page...
            </motion.div>
          )}
        </AnimatePresence>

        <WhatsAppSupportButton />

        <HeroSection openRegistration={handleManualModalOpen} />

        {/* Problem Section - WORKING PROFESSIONAL FOCUSED */}
        <motion.section
          className="problem-section"
          initial="initial"
          whileInView="whileInView"
          transition={sectionFadeIn.transition}
          viewport={sectionFadeIn.viewport}
        >
          <div className="container">
            <motion.div className="section-content">
              <motion.h2 {...sectionFadeIn}>
                😰 Working Professionals: Do You Face These Struggles?
              </motion.h2>

              <motion.p
                className="problem-intro"
                {...sectionFadeIn}
                transition={{ delay: 0.1, ...sectionFadeIn.transition }}
              >
                All working professionals in our community were facing these exact problems before they discovered the digital income system.
              </motion.p>

              <motion.div
                className="problem-grid"
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
              >
                {[
                  {
                    emoji: '⏰',
                    title: 'Trading Time for Money?',
                    desc: 'Your income is directly tied to the hours you work. Office pogra, work panra, salary varudhu. But no work = no income. Scalability illa.'
                  },
                  {
                    emoji: '💸',
                    title: 'Salary Growth Too Slow?',
                    desc: 'Every year appraisal ku wait panra. But increment romba kammi. Your skills are growing faster than your salary. Frustration aagudhaa?'
                  },
                  {
                    emoji: '😔',
                    title: 'No Work-Life Balance?',
                    desc: 'Office, deadlines, meetings, calls — repeat. Time romba kammi. You barely get time for yourself, family, or passion projects.'
                  },
                  {
                    emoji: '🔒',
                    title: 'Knowledge Locked Inside Job?',
                    desc: 'You have years of experience and valuable skills. But all of it is only used during work hours. Your knowledge deserves more leverage.'
                  },
                  {
                    emoji: '📱',
                    title: 'Expertise Not Monetized?',
                    desc: 'You\'re an expert in your field. But your knowledge isn\'t converted into digital products, courses, or consulting that can earn beyond your job.'
                  },
                  {
                    emoji: '💰',
                    title: 'No Passive Income?',
                    desc: 'Every rupee you earn requires your active presence. Leave edutha, income stop aagudhaa? No scalable income streams beyond salary.'
                  },
                  {
                    emoji: '🤯',
                    title: 'Can\'t Scale Impact?',
                    desc: 'You want to help more people with your expertise. But physical limitations prevent you from reaching thousands who need your knowledge.'
                  },
                  {
                    emoji: '💔',
                    title: 'No Digital Presence?',
                    desc: 'Other professionals are building authority online and reaching global audiences. You\'re still limited to your 9-5 role.'
                  },
                  {
                    emoji: '🎯',
                    title: 'Expertise Not Leveraged?',
                    desc: 'Your professional expertise is an asset. But it\'s not being leveraged into digital products, services, or systems that can multiply your income.'
                  }
                ].map((problem) => (
                  <motion.div
                    key={problem.title}
                    className="problem-card"
                    variants={itemVariants}
                    whileHover={{ y: -8, transition: { duration: 0.2 } }}
                  >
                    <h3>
                      {problem.emoji} {problem.title}
                    </h3>
                    <p>{problem.desc}</p>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </motion.section>

        {/* Solution Section - WORKING PROFESSIONAL FOCUSED */}
        <motion.section
          className="solution-section"
          initial="initial"
          whileInView="whileInView"
          transition={sectionFadeIn.transition}
          viewport={sectionFadeIn.viewport}
        >
          <div className="container">
            <motion.div className="section-content">
              <motion.h2 {...sectionFadeIn}>
                🎯 Traditional Job vs Digital Knowledge Asset
              </motion.h2>

              <motion.p
                className="solution-intro"
                {...sectionFadeIn}
                transition={{ delay: 0.1, ...sectionFadeIn.transition }}
              >
                What if you could convert your knowledge into digital products that work for you 24/7? <strong>System irundha, income random illa. It becomes predictable and automated.</strong>
              </motion.p>

              <div className="comparison-table">
                <motion.div
                  className="old-way"
                  {...sectionFadeIn}
                  transition={{ delay: 0.2, ...sectionFadeIn.transition }}
                >
                  <h3>❌ Traditional Job Model</h3>
                  <ul>
                    <li>Income only when you work</li>
                    <li>Limited by your working hours</li>
                    <li>Salary growth is slow and capped</li>
                    <li>No passive income streams</li>
                    <li>Impact limited by your daily capacity</li>
                    <li>Can't take breaks without losing income</li>
                    <li>Knowledge and expertise underutilized</li>
                    <li>No digital leverage or scalability</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="new-way"
                  {...sectionFadeIn}
                  transition={{ delay: 0.4, ...sectionFadeIn.transition }}
                >
                  <h3>✅ Digital Knowledge Asset (AI-Powered)</h3>
                  <ul>
                    <li>Income 24/7 through digital products</li>
                    <li>Not limited by working hours</li>
                    <li>Income can grow exponentially</li>
                    <li>Multiple scalable income streams</li>
                    <li>Impact multiplied through digital reach</li>
                    <li>Passive income while you rest</li>
                    <li>Knowledge converted to digital assets</li>
                    <li>Full digital leverage and automation</li>
                  </ul>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </motion.section>

        {/* Mentor Section */}
        <section className="mentor-section py-16 bg-[#0a2540] text-gray-200">
          <div className="container mx-auto px-4">
            <motion.div
              className="mentor-content grid items-center gap-10 lg:grid-cols-2"
              initial="initial"
              whileInView="whileInView"
              transition={sectionFadeIn.transition}
              viewport={sectionFadeIn.viewport}
            >
              <div className="flex justify-center lg:justify-end">
                <motion.div
                  className="rounded-3xl border-2 border-[#8b5cf6] p-3 shadow-lg shadow-[#8b5cf6]/20 bg-[#1e3a5f]"
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                >
                  <img
                    src="https://storage.files-vault.com/uploads/1761032692-VSdwsp8AG0.png"
                    alt="Abdul Hakkim - Digital Income Expert"
                    loading="lazy"
                    className="rounded-2xl w-full max-w-[360px] md:max-w-[400px] object-cover"
                  />
                </motion.div>
              </div>

              <div className="mentor-info max-w-xl mx-auto lg:mx-0">
                <motion.h2
                  className="text-3xl md:text-4xl font-bold text-[#8b5cf6] mb-6 flex items-center gap-2"
                  {...sectionFadeIn}
                >
                  🎯 <span>Meet Your Mentor</span>
                </motion.h2>

                <motion.p
                  className="text-base md:text-lg leading-relaxed mb-6 text-gray-300"
                  {...sectionFadeIn}
                  transition={{ delay: 0.1, ...sectionFadeIn.transition }}
                >
                  நான் Abdul Hakkim. பல Working Professionals திறமையும் அனுபவமும் இருந்தும், digital transformation இல்லாததால் salary-க்குள் சிக்கி தவிப்பதைப் பார்த்திருக்கிறேன்.
                </motion.p>

                <motion.ul
                  className="space-y-4 text-gray-300 text-base md:text-lg"
                  variants={containerVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.2 }}
                >
                  {[
                    {
                      emoji: '👉',
                      color: 'text-yellow-400',
                      title: 'The Real Problem:',
                      desc: 'Professional expertise இருக்கு, but digital leverage இல்லாததால் income and impact limited ஆகிறது.'
                    },
                    {
                      emoji: '✅',
                      color: 'text-green-400',
                      title: 'My Solution:',
                      desc: 'நான் உருவாக்கிய "Digital Knowledge System" உங்கள் expertise-ஐ 24/7 monetize செய்து, passive income கொடுக்கும்.'
                    },
                    {
                      emoji: '📈',
                      color: 'text-blue-400',
                      title: 'Proven Results:',
                      desc: 'Helped 100+ Working Professionals build digital assets earning ₹5-25 Lakhs additional income annually.'
                    },
                    {
                      emoji: '🗣️',
                      color: 'text-purple-400',
                      title: 'Easy Implementation:',
                      desc: 'Tamil-ல எளிய முறையில் digital transformation கற்றுக்கொள்ளலாம். No tech skills required.'
                    }
                  ].map((item) => (
                    <motion.li key={item.title} className="flex items-start gap-3" variants={itemVariants}>
                      <span className={`${item.color} text-xl mt-1`}>{item.emoji}</span>
                      <div className="flex-1">
                        <span className="font-medium">{item.title}</span>
                        <div className="text-gray-300 mt-1">{item.desc}</div>
                      </div>
                    </motion.li>
                  ))}
                </motion.ul>

                <motion.p
                  className="text-base md:text-lg leading-relaxed mt-6 text-gray-300"
                  {...sectionFadeIn}
                  transition={{ delay: 0.2, ...sectionFadeIn.transition }}
                >
                  உங்கள் Professional Career-ஐ அடுத்த கட்டத்திற்கு கொண்டு செல்ல, இதுவே சரியான வாய்ப்பு. Let's Transform! 🚀
                </motion.p>

                <motion.div
                  className="mt-6 flex flex-wrap gap-4"
                  {...sectionFadeIn}
                  transition={{ delay: 0.3, ...sectionFadeIn.transition }}
                >
                  <div className="flex items-center gap-2 text-sm bg-gray-700 px-3 py-1 rounded-full">
                    <FiStar className="text-[#8b5cf6]" />
                    <span>Digital Income Expert</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm bg-gray-700 px-3 py-1 rounded-full">
                    <FiStar className="text-[#8b5cf6]" />
                    <span>Proven Professional Success</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm bg-gray-700 px-3 py-1 rounded-full">
                    <FiStar className="text-[#8b5cf6]" />
                    <span>Tamil Digital Transformation</span>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Testimonials Section */}
        <motion.section
          className="testimonials-section"
          initial="initial"
          whileInView="whileInView"
          transition={sectionFadeIn.transition}
          viewport={sectionFadeIn.viewport}
        >
          <div className="container">
            <motion.div className="section-content">
              <motion.h2 {...sectionFadeIn}>
                🎬 Success Stories - Video Testimonials
              </motion.h2>

              <motion.p
                className="testimonials-subtitle"
                {...sectionFadeIn}
                transition={{ delay: 0.1, ...sectionFadeIn.transition }}
              >
                Real results from professionals who built digital assets
              </motion.p>

              <motion.div
                className="video-grid"
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
              >
                {testimonialVideos.map((video) => (
                  <motion.div
                    key={video.id}
                    className="video-card"
                    onClick={() => {
                      setSelectedVideo(video);
                      if (typeof fbq !== 'undefined') {
                        fbq('track', 'ViewContent', {
                          content_name: video.title,
                          content_type: 'video'
                        });
                      }
                    }}
                    variants={itemVariants}
                    whileHover={{ scale: 1.02, y: -8 }}
                    whileTap={{ scale: 0.98 }}
                    tabIndex={0}
                    role="button"
                    aria-label={`Play testimonial video from ${video.name}`}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setSelectedVideo(video);
                      }
                    }}
                  >
                    <div className="video-thumbnail">
                      <img
                        src={video.thumbnail}
                        alt={`${video.title} thumbnail`}
                        loading="lazy"
                        width="300"
                        height="200"
                      />
                      <div className="play-overlay">
                        <FiPlay className="play-icon" />
                      </div>
                    </div>
                    <div className="video-info">
                      <h4>{video.title}</h4>
                      <p>{video.name}</p>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </motion.section>

        {/* Client Feedback Section */}
        <motion.section
          className="client-feedback-section"
          initial="initial"
          whileInView="whileInView"
          transition={sectionFadeIn.transition}
          viewport={sectionFadeIn.viewport}
        >
          <div className="container">
            <motion.div className="section-content">
              <motion.h2 {...sectionFadeIn}>What Other Professionals Say</motion.h2>

              <motion.p
                className="feedback-subtitle"
                {...sectionFadeIn}
                transition={{ delay: 0.1, ...sectionFadeIn.transition }}
              >
                Join hundreds of successful Working Professionals using Digital Systems
              </motion.p>

              <motion.div
                className="feedback-filter"
                {...sectionFadeIn}
                transition={{ delay: 0.2, ...sectionFadeIn.transition }}
              >
                <FiFilter className="filter-icon" />
                <select
                  value={selectedRoleFilter}
                  onChange={(e) => setSelectedRoleFilter(e.target.value)}
                  className="role-filter-select"
                  aria-label="Filter by profession"
                >
                  {uniqueRoles.map((role) => (
                    <option key={role} value={role}>
                      {role === 'all' ? 'All Professions' : role}
                    </option>
                  ))}
                </select>
              </motion.div>

              <motion.div
                className="feedback-grid"
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
              >
                {filteredFeedbacks.map((feedback) => (
                  <motion.div
                    key={feedback.id}
                    className="feedback-card"
                    variants={itemVariants}
                    whileHover={{ scale: 1.02, y: -8 }}
                    whileTap={{ scale: 0.98 }}
                    tabIndex={0}
                    role="article"
                    aria-labelledby={`feedback-name-${feedback.id}`}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleFeedbackExpanded(feedback.id);
                      }
                    }}
                  >
                    <div className="feedback-image-container">
                      {feedback.image ? (
                        <img
                          src={feedback.image}
                          alt={`${feedback.name} - ${feedback.role}`}
                          loading="lazy"
                          width="300"
                          height="200"
                          className="feedback-rect-image"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div className="avatar-fallback-rect">
                        {getInitials(feedback.name)}
                      </div>
                    </div>

                    <div className="feedback-info">
                      <h3 id={`feedback-name-${feedback.id}`}>{feedback.name}</h3>
                      <p className="feedback-role">{feedback.role}</p>
                    </div>

                    <div className="feedback-quote">
                      <p>
                        {expandedFeedback[feedback.id] || feedback.quote.length <= 140
                          ? feedback.quote
                          : `${feedback.quote.slice(0, 140)}...`}
                      </p>
                      {feedback.quote.length > 140 && (
                        <button
                          className="read-more-btn"
                          onClick={() => toggleFeedbackExpanded(feedback.id)}
                          aria-expanded={!!expandedFeedback[feedback.id]}
                          aria-controls={`feedback-quote-${feedback.id}`}
                        >
                          {expandedFeedback[feedback.id] ? 'Read less' : 'Read more'}
                        </button>
                      )}
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </motion.section>

        {/* Bonus Section - WORKING PROFESSIONAL FOCUSED */}
        <motion.section
          className="bonus-section"
          initial="initial"
          whileInView="whileInView"
          transition={sectionFadeIn.transition}
          viewport={sectionFadeIn.viewport}
        >
          <div className="container">
            <motion.div className="section-content">
              <motion.h2 {...sectionFadeIn}>
                🎁 Exclusive Bonus Vault for Working Professionals (Worth ₹35,000)
              </motion.h2>

              <motion.div
                className="bonus-grid"
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
              >
                {[
                  {
                    img: 'https://storage.files-vault.com/uploads/1760966854-BXbDBFXvqB.png',
                    emoji: '🤖',
                    title: 'AI Tools for Professional Growth',
                    desc: 'Top 50+ AI tools to automate your work, boost productivity, and create digital products (Worth ₹10,000)'
                  },
                  {
                    img: 'https://storage.files-vault.com/uploads/1760966871-XKw3XTHpgg.png',
                    emoji: '📊',
                    title: 'Digital Product Templates',
                    desc: 'Ready-to-use templates for creating online courses, consulting systems, and knowledge products (Worth ₹12,000)'
                  },
                  {
                    img: 'https://storage.files-vault.com/uploads/1760976790-fakmsXQpOV.png',
                    emoji: '💬',
                    title: 'Private Professionals Community',
                    desc: 'Lifetime access to exclusive Tamil Working Professionals AI & Digital Income group (Worth ₹8,000)'
                  },
                  {
                    img: 'https://storage.googleapis.com/msgsndr/Te8QGpu12zbMxZIVQlZi/media/6974640bc1fa0c55da395de0.png',
                    emoji: '🗺️',
                    title: 'Free Digital Income Roadmap',
                    desc: 'Personalized step-by-step PDF guide to launch your AI-powered Digital Income System (Worth ₹5,000)'
                  }
                ].map((bonus) => (
                  <motion.div
                    key={bonus.title}
                    className="bonus-item"
                    variants={itemVariants}
                    whileHover={{ y: -8, scale: 1.02 }}
                  >
                    <img
                      src={bonus.img}
                      alt={bonus.title}
                      className="bonus-image"
                      width="120"
                      height="160"
                      loading="lazy"
                    />
                    <h3>
                      {bonus.emoji} {bonus.title}
                    </h3>
                    <p>{bonus.desc}</p>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </motion.section>

        {/* FAQ Section */}
        <motion.section
          className="faq-section"
          initial="initial"
          whileInView="whileInView"
          transition={sectionFadeIn.transition}
          viewport={sectionFadeIn.viewport}
        >
          <div className="container">
            <motion.div className="section-content">
              <motion.h2 {...sectionFadeIn}>Frequently Asked Questions</motion.h2>

              <motion.p
                className="faq-subtitle"
                {...sectionFadeIn}
                transition={{ delay: 0.1, ...sectionFadeIn.transition }}
              >
                Got questions? We've got answers. Find everything you need to know about our 3-hour workshop.
              </motion.p>

              <motion.div
                className="faq-search"
                {...sectionFadeIn}
                transition={{ delay: 0.2, ...sectionFadeIn.transition }}
              >
                <FiSearch className="search-icon" />
                <input
                  type="text"
                  placeholder="Search questions..."
                  value={faqSearch}
                  onChange={(e) => handleFaqSearch(e.target.value)}
                  className="search-input"
                  aria-label="Search frequently asked questions"
                />
              </motion.div>

              <div className="faq-accordion">
                {filteredFaqs.map((faq) => (
                  <motion.div key={faq.id} className="faq-item" layout>
                    <button
                      className="faq-question"
                      onClick={() => toggleFaq(faq.id)}
                      aria-expanded={expandedFaq === faq.id}
                      aria-controls={`faq-answer-${faq.id}`}
                      id={`faq-question-${faq.id}`}
                    >
                      <span>{faq.question}</span>
                      <FiChevronDown
                        className={`chevron-icon ${expandedFaq === faq.id ? 'expanded' : ''}`}
                      />
                    </button>

                    <AnimatePresence>
                      {expandedFaq === faq.id && (
                        <motion.div
                          id={`faq-answer-${faq.id}`}
                          className="faq-answer"
                          role="region"
                          aria-labelledby={`faq-question-${faq.id}`}
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3, ease: 'easeInOut' }}
                        >
                          <div
                            className="faq-answer-content"
                            dangerouslySetInnerHTML={{ __html: faq.answer }}
                          />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                ))}
              </div>

              {filteredFaqs.length === 0 && (
                <motion.div
                  className="faq-no-results"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  <p>No questions found matching your search. Try different keywords.</p>
                </motion.div>
              )}
            </motion.div>
          </div>
        </motion.section>

        {/* Final CTA Section */}
        <motion.section
          className="final-cta"
          initial="initial"
          whileInView="whileInView"
          transition={sectionFadeIn.transition}
          viewport={sectionFadeIn.viewport}
        >
          <div className="container">
            <motion.div className="cta-content">
              <motion.h2 {...sectionFadeIn}>
                🔥 Limited Time Offer - Only 50 Seats Available!
              </motion.h2>

              <motion.p
                className="cta-subtitle"
                {...sectionFadeIn}
                transition={{ delay: 0.1, ...sectionFadeIn.transition }}
              >
                3-Hour Workshop Value: ₹4,997 | Today: **100% FREE!**
              </motion.p>

              <WorkshopTimer className="large" />

              <motion.button
                className="cta-primary large"
                onClick={() => {
                  handleManualModalOpen();
                  if (typeof fbq !== 'undefined') {
                    fbq('track', 'InitiateCheckout', {
                      content_name: 'Digital Income Workshop - Final CTA',
                      content_category: 'Workshop',
                      value: 0,
                      currency: 'INR'
                    });
                  }
                }}
                {...sectionFadeIn}
                transition={{ delay: 0.4, ...sectionFadeIn.transition }}
                whileHover={{ scale: 1.05, y: -4 }}
                whileTap={{ scale: 0.95 }}
              >
                Yes! Reserve My FREE Seat Now 🚀
              </motion.button>

              <motion.div
                className="guarantee-text"
                {...sectionFadeIn}
                transition={{ delay: 0.5, ...sectionFadeIn.transition }}
              >
                <FiCheck className="check-icon" />
                <span>100% FREE - No Hidden Charges - Worth ₹4,997</span>
              </motion.div>
            </motion.div>
          </div>
        </motion.section>

        {/* Registration Modal */}
        <AnimatePresence>
          {isFormModalOpen && (
            <motion.div
              className="modal-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={(e) => {
                if (e.target === e.currentTarget) setIsFormModalOpen(false);
              }}
            >
              <motion.div
                className="modal-content"
                id="registration-modal"
                initial={{ opacity: 0, scale: 0.8, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.8, y: 20 }}
                transition={{ type: 'spring', stiffness: 300, damping: 25 }}
                ref={modalRef}
              >
                <div className="modal-header">
                  <h3>🚀 Register for the FREE 3-Hour Digital Income Workshop!</h3>
                  <button
                    className="close-button"
                    onClick={() => setIsFormModalOpen(false)}
                    aria-label="Close registration form"
                  >
                    <FiX />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="registration-form">
                  <div className="form-group">
                    <label htmlFor="name">
                      <FiUser className="input-icon" />
                      Full Name *
                    </label>
                    <input
                      ref={firstInputRef}
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      placeholder="Enter your full name"
                      className={formErrors.name ? 'error' : ''}
                      aria-describedby={formErrors.name ? 'name-error' : undefined}
                    />
                    {formErrors.name && (
                      <span id="name-error" className="error-message" role="alert">
                        {formErrors.name}
                      </span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">
                      <FiMail className="input-icon" />
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="Enter your email address"
                      className={formErrors.email ? 'error' : ''}
                      aria-describedby={formErrors.email ? 'email-error' : undefined}
                    />
                    {formErrors.email && (
                      <span id="email-error" className="error-message" role="alert">
                        {formErrors.email}
                      </span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone">
                      <FiPhone className="input-icon" />
                      Phone Number *
                    </label>
                    <div className="phone-input-group">
                      <SearchableCountryCodeSelect
                        options={countryCodes}
                        value={formData.countryCode}
                        onChange={(code) => handleInputChange('countryCode', code)}
                      />
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={(e) =>
                          handleInputChange('phone', e.target.value.replace(/\D/g, ''))
                        }
                        placeholder="Enter your phone number"
                        className={formErrors.phone ? 'error' : ''}
                        aria-describedby={formErrors.phone ? 'phone-error' : undefined}
                        maxLength="15"
                      />
                    </div>
                    {formErrors.phone && (
                      <span id="phone-error" className="error-message" role="alert">
                        {formErrors.phone}
                      </span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="profession">
                      <FiBriefcase className="input-icon" />
                      Your Profession *
                    </label>
                    <select
                      id="profession"
                      name="profession"
                      value={formData.profession}
                      onChange={(e) => handleInputChange('profession', e.target.value)}
                      className={formErrors.profession ? 'error' : ''}
                      aria-describedby={formErrors.profession ? 'profession-error' : undefined}
                    >
                      <option value="">Select your profession</option>
                      <option value="IT Professional">IT Professional</option>
                      <option value="Software Developer">Software Developer</option>
                      <option value="Manager">Manager</option>
                      <option value="Consultant">Consultant</option>
                      <option value="Corporate Employee">Corporate Employee</option>
                      <option value="Teacher/Professor">Teacher/Professor</option>
                      <option value="Banker">Banker</option>
                      <option value="Accountant">Accountant</option>
                      <option value="Engineer">Engineer</option>
                      <option value="Other Working Professional">Other Working Professional</option>
                    </select>
                    {formErrors.profession && (
                      <span id="profession-error" className="error-message" role="alert">
                        {formErrors.profession}
                      </span>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.consent}
                        onChange={(e) => handleInputChange('consent', e.target.checked)}
                        className={formErrors.consent ? 'error' : ''}
                        aria-describedby={formErrors.consent ? 'consent-error' : undefined}
                      />
                      <span className="checkmark"></span>
                      I agree to receive updates about the program and understand the terms &
                      conditions *
                    </label>
                    {formErrors.consent && (
                      <span id="consent-error" className="error-message" role="alert">
                        {formErrors.consent}
                      </span>
                    )}
                  </div>

                  <button type="submit" className="submit-button" disabled={isSubmitting}>
                    {isSubmitting ? 'Processing...' : 'Register Now - 100% FREE 🚀'}
                  </button>

                  <p className="form-note">
                    After registration, you'll receive workshop details via email and WhatsApp.
                  </p>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Video Modal */}
        <AnimatePresence>
          {selectedVideo && (
            <motion.div
              className="video-modal-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={(e) => {
                if (e.target === e.currentTarget) setSelectedVideo(null);
              }}
            >
              <motion.div
                className="video-modal-content"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ type: 'spring', stiffness: 300, damping: 25 }}
              >
                <div className="video-modal-header">
                  <h3>{selectedVideo.title}</h3>
                  <button
                    className="close-button"
                    onClick={() => setSelectedVideo(null)}
                    aria-label="Close video"
                  >
                    <FiX />
                  </button>
                </div>

                <div className="video-embed-container">
                  <iframe
                    src={`https://www.youtube.com/embed/${selectedVideo.embedId}?autoplay=1&rel=0`}
                    title={selectedVideo.title}
                    allow="accelerometer;autoplay;clipboard-write;encrypted-media;gyroscope;picture-in-picture"
                    allowFullScreen
                    loading="lazy"
                  ></iframe>
                </div>

                <div className="video-fallback">
                  <p>
                    Can't view the video?{' '}
                    <a
                      href={selectedVideo.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Open on YouTube
                    </a>
                  </p>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Sticky Footer */}
        <motion.div
          className="sticky-footer"
          initial={{ y: '100%' }}
          animate={{ y: '0%' }}
          transition={{ duration: 0.7, ease: 'easeInOut', delay: 1.5 }}
        >
          <motion.div
            className="footer-info-box"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 2.2 }}
          >
            <div className="date">Next Workshop:</div>
            <div className="time">{workshopDetails.formattedDate}</div>
          </motion.div>

          <motion.button
            className="footer-cta-button"
            onClick={() => {
              handleManualModalOpen();
              if (typeof fbq !== 'undefined') {
                fbq('track', 'InitiateCheckout', {
                  content_name: 'Digital Income Workshop - Sticky Footer CTA',
                  content_category: 'Workshop',
                  value: 0,
                  currency: 'INR'
                });
              }
            }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            animate={{
              scale: [1, 1.03, 1],
              transition: {
                duration: 1.5,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: 2.5
              }
            }}
          >
            Register FREE Now 🚀
          </motion.button>
        </motion.div>

        <footer className="footer">
          <div className="container">
            <div className="footer-content">
              <p>&copy; 2026 10xAI Digital Income. All rights reserved.</p>
              <p style={{ fontSize: '0.8rem', opacity: '0.7', marginTop: '1rem' }}>
                This site is not part of the Facebook website or Facebook Inc. Additionally, this
                site is NOT endorsed by Facebook. FACEBOOK is a trademark of FACEBOOK, Inc.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}

export default App;